import React, { useState } from 'react';
import { Package, BarChart3, PieChart, RefreshCw, ArrowRightLeft, Warehouse, HardHat } from 'lucide-react';
import { usePermissions } from 'hooks/usePermissions';

// Importar componentes das sub-abas
import ProdutosUsadosOS from './SubAbas/ProdutosUsadosOS';
import ProdutosAlmoxarifadosFiltrados from './SubAbas/ProdutosAlmoxarifadosFiltrados';
import BackupInfra from './SubAbas/BackupInfra';
import FerramentasEpis from './SubAbas/FerramentasEpis';
import EquipamentosParaTestes from './SubAbas/EquipamentosParaTestes';
import OSSEstoque from './SubAbas/OSSEstoque';
import EntradasNFEstoque from './SubAbas/EntradasNFEstoque';
import MovimentacaoInterna from './SubAbas/MovimentacaoInterna';
// import EntradasNFServicos from './SubAbas/EntradasNFServicos'; // Arquivo não existe

const ENTRADAS_NF_PERMISSION = 'estoque.entradas_nf.view';

const EstoqueOperacional = () => {
  const { hasPermission } = usePermissions();
  const [subAbaAtiva, setSubAbaAtiva] = useState('produtos-usados-os');

  // Verificar permissões após todos os hooks
  if (!hasPermission('estoque.view')) {
    return (
      <div className="min-h-screen bg-[color:var(--surface-muted)] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">Acesso Negado</h1>
          <p className="text-[color:var(--text-muted)]">Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  // Sub-abas disponíveis
  const subAbas = [
    {
      id: 'produtos-usados-os',
      nome: 'Produtos Usados em OS',
      icon: Package
    },
    {
      id: 'produtos-almoxarifados-filtrados',
      nome: 'Produtos Almoxarifados Filtrados',
      icon: BarChart3
    },
    {
      id: 'backup-infra',
      nome: 'Estoque de Backup - Infra',
      icon: Warehouse
    },
    {
      id: 'ferramentas-epis',
      nome: 'Ferramentas e EPIs Filtrados',
      icon: HardHat
    },
    {
      id: 'equipamentos-para-testes',
      nome: 'Equipamentos para Testes',
      icon: PieChart
    },
    {
      id: 'oss-estoque',
      nome: 'OSs Estoque',
      icon: RefreshCw
    },
    {
      id: 'entradas-nf-estoque',
      nome: 'Entradas de NF - Estoque',
      icon: Package,
      permission: ENTRADAS_NF_PERMISSION
    },
    {
      id: 'movimentacao-interna',
      nome: 'Movimentação Interna',
      icon: ArrowRightLeft
    },
  ];

  const subAbasPermitidas = subAbas.filter((aba) => !aba.permission || hasPermission(aba.permission));

  const renderizarSubAba = () => {
    switch (subAbaAtiva) {
      case 'produtos-usados-os':
        return <ProdutosUsadosOS />;
      case 'produtos-almoxarifados-filtrados':
        return <ProdutosAlmoxarifadosFiltrados />;
      case 'backup-infra':
        return <BackupInfra />;
      case 'ferramentas-epis':
        return <FerramentasEpis />;
      case 'equipamentos-para-testes':
        return <EquipamentosParaTestes />;
      case 'oss-estoque':
        return <OSSEstoque />;
      case 'entradas-nf-estoque':
        if (!hasPermission(ENTRADAS_NF_PERMISSION)) {
          return (
            <div className="min-h-[320px] bg-[color:var(--surface-muted)] flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-[color:var(--text-primary)] mb-2">Acesso Negado</h1>
                <p className="text-[color:var(--text-muted)]">Você não tem permissão para acessar Entradas de NF.</p>
              </div>
            </div>
          );
        }
        return <EntradasNFEstoque />;
      case 'movimentacao-interna':
        return <MovimentacaoInterna />;
      default:
        return <div>Sub-aba não encontrada</div>;
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--surface-muted)] w-full">
      {/* Navegação das sub-abas: pílulas que quebram linha no desktop, select nativo no celular. */}
      <nav aria-label="Sub-abas de Estoque" className="bg-[color:var(--surface)] border-b border-[color:var(--border)] w-full px-4 sm:px-6 lg:px-8 py-3">
        <label className="sm:hidden block">
          <span className="sr-only">Sub-aba</span>
          <select
            value={subAbaAtiva}
            onChange={(event) => setSubAbaAtiva(event.target.value)}
            className="w-full rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5 text-sm font-medium text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
          >
            {subAbasPermitidas.map((aba) => (
              <option key={aba.id} value={aba.id}>{aba.nome}</option>
            ))}
          </select>
        </label>
        <div className="hidden sm:flex flex-wrap gap-1.5">
          {subAbasPermitidas.map((aba) => {
            const IconComponent = aba.icon;
            const ativa = subAbaAtiva === aba.id;
            return (
              <button
                key={aba.id}
                type="button"
                aria-current={ativa ? 'page' : undefined}
                onClick={() => setSubAbaAtiva(aba.id)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] ${
                  ativa
                    ? 'bg-[color:var(--accent)]/10 text-[color:var(--accent)] ring-1 ring-inset ring-[color:var(--accent)]/35'
                    : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                }`}
              >
                <IconComponent className="h-4 w-4 shrink-0" aria-hidden="true" />
                {aba.nome}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Conteúdo da sub-aba ativa */}
      <div className="px-4 sm:px-6 lg:px-8 py-6 w-full">
        {renderizarSubAba()}
      </div>
    </div>
  );
};

export default EstoqueOperacional;
